import { useMemo, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUserDirectory } from '@/api/users';
import { useClientsList } from '@/api/clients';
import { useAvailabilityOverlapCommitments } from '@/api/reports';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import {
    computeOverlap,
    formatMin,
    totalCommitmentMinutes,
    totalOverlapMinutes,
    type Commitment,
    type CommitmentInterval,
    type Interval,
    type OverlapDay,
} from '@/lib/availabilityOverlap';
import { DAY_LABELS } from '@/lib/availability';

/**
 * Availability Overlap — weekly grid comparing one staff member's availability
 * against one client's availability, with the staff's active recurring
 * commitments overlaid on the overlap band. Useful when evaluating whether a
 * staff member can fit a new client into their week.
 *
 * All computation runs client-side from cached directory data; only the
 * staff's recurring commitments require a server round-trip (see
 * `useAvailabilityOverlapCommitments`).
 */

const GRID_START_MIN = 6 * 60;   // 6:00 AM
const GRID_END_MIN = 22 * 60;    // 10:00 PM
const GRID_SPAN = GRID_END_MIN - GRID_START_MIN;
const HOUR_TICKS: number[] = [];
for (let h = 6; h <= 22; h += 2) HOUR_TICKS.push(h * 60);

export function AvailabilityOverlapPage() {
    const staffQuery = useUserDirectory();
    const clientsQuery = useClientsList();

    const [staffId, setStaffId] = useState<string>('');
    const [clientId, setClientId] = useState<string>('');
    const [overlapOnly, setOverlapOnly] = useState(false);

    const commitmentsQuery = useAvailabilityOverlapCommitments(staffId || null, clientId || null);

    const staff = useMemo(
        () => staffQuery.data?.find((u) => u.id === staffId) ?? null,
        [staffQuery.data, staffId],
    );
    const client = useMemo(
        () => clientsQuery.data?.find((c) => c.id === clientId) ?? null,
        [clientsQuery.data, clientId],
    );

    const commitments: Commitment[] = useMemo(
        () => (commitmentsQuery.data?.commitments ?? []).map((c) => ({
            seriesId:   c.seriesId,
            clientId:   c.clientId,
            clientName: c.clientName,
            dayOfWeek:  c.dayOfWeek,
            startMin:   c.startMin,
            endMin:     c.endMin,
        })),
        [commitmentsQuery.data],
    );

    const days = useMemo(
        () => computeOverlap(staff?.availability, client?.availability, commitments),
        [staff, client, commitments],
    );

    const staffOptions = useMemo(() => {
        const rows = staffQuery.data ?? [];
        return [...rows].sort((a, b) =>
            `${a.last} ${a.first}`.localeCompare(`${b.last} ${b.first}`),
        );
    }, [staffQuery.data]);

    const clientOptions = useMemo(() => {
        const rows = clientsQuery.data ?? [];
        return rows
            .filter((c) => c.status === 'Active')
            .sort((a, b) => `${a.last} ${a.first}`.localeCompare(`${b.last} ${b.first}`));
    }, [clientsQuery.data]);

    const showGrid = !!staff && !!client;

    const totalOverlap = showGrid ? totalOverlapMinutes(days) : 0;
    const totalCommit  = showGrid ? totalCommitmentMinutes(days) : 0;
    const totalFree    = Math.max(0, totalOverlap - totalCommit);

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div>
                <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                    <ArrowLeft className="w-3 h-3" /> All reports
                </Link>
                <h1 className="font-display text-2xl font-bold text-text">Availability Overlap</h1>
                <p className="text-xs text-text-subtle">
                    Weekly view comparing a staff member's availability to a client's availability, with the staff's active recurring commitments on other clients shaded on the staff bar.
                </p>
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">Staff</span>
                            <select
                                value={staffId}
                                onChange={(e) => setStaffId(e.target.value)}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">Select staff…</option>
                                {staffOptions.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.last}, {s.first} · {s.roleName}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Client</span>
                            <select
                                value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">Select client…</option>
                                {clientOptions.map((c) => (
                                    <option key={c.id} value={c.id}>{c.last}, {c.first}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </CardBody>
            </Card>

            {!showGrid && (
                <Card>
                    <CardBody>
                        <p className="text-sm text-text-subtle">Pick a staff member and a client to see their weekly overlap.</p>
                    </CardBody>
                </Card>
            )}

            {showGrid && (
                <>
                    <div className="grid grid-cols-3 gap-3">
                        <Kpi label="Staff/client overlap" value={formatHours(totalOverlap)} hint="Raw weekly overlap between staff and client availability." />
                        <Kpi label="Staff commitments in overlap" value={formatHours(totalCommit)} hint="Time already booked on the staff's other active recurring series." />
                        <Kpi label="Uncommitted overlap" value={formatHours(totalFree)} hint="Overlap minus commitments — the room you have to schedule this client." />
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {staff ? `${staff.first} ${staff.last}` : '—'}
                                {' '}×{' '}
                                {client ? `${client.first} ${client.last}` : '—'}
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="pt-0 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <Legend />
                                <label className="inline-flex items-center gap-2 text-xs text-text-muted cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={overlapOnly}
                                        onChange={(e) => setOverlapOnly(e.target.checked)}
                                        className="h-3.5 w-3.5 accent-accent"
                                    />
                                    Overlap only
                                </label>
                            </div>
                            {commitmentsQuery.isLoading ? (
                                <div className="flex items-center gap-2 text-text-subtle text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading commitments…
                                </div>
                            ) : (
                                <OverlapGrid days={days} overlapOnly={overlapOnly} />
                            )}
                        </CardBody>
                    </Card>

                    <Alert variant="info">
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-text">What this accounts for</p>
                            <ul className="text-xs text-text-subtle list-disc pl-4 space-y-0.5">
                                <li>Weekly availability patterns from the staff record and the client record.</li>
                                <li>The staff's active recurring series (next 28 days), excluding any series for the selected client.</li>
                            </ul>
                            <p className="text-xs font-medium text-text pt-2">What this does NOT account for</p>
                            <ul className="text-xs text-text-subtle list-disc pl-4 space-y-0.5">
                                <li>One-off (non-recurring) sessions already on the staff's calendar.</li>
                                <li>PTO, holidays, or time-off requests.</li>
                                <li>Authorization end dates, client/staff status changes, or travel time between appointments.</li>
                            </ul>
                        </div>
                    </Alert>
                </>
            )}
        </div>
    );
}

function OverlapGrid({ days, overlapOnly }: { days: OverlapDay[]; overlapOnly: boolean }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-2xs text-text-subtle">
                <div className="w-14 shrink-0" />
                {!overlapOnly && <div className="w-12 shrink-0" />}
                <div className="relative flex-1 h-4">
                    {HOUR_TICKS.map((min) => (
                        <div
                            key={min}
                            className="absolute top-0 h-full border-l border-border"
                            style={{ left: `${positionPct(min)}%` }}
                        >
                            {min < GRID_END_MIN && (
                                <span className="absolute top-0 left-1 whitespace-nowrap">{formatMin(min)}</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            {days.map((d) => (
                <DayRow key={d.day} day={d} overlapOnly={overlapOnly} />
            ))}
        </div>
    );
}

function DayRow({ day, overlapOnly }: { day: OverlapDay; overlapOnly: boolean }) {
    if (overlapOnly) {
        return (
            <div className="flex items-center gap-2">
                <div className="w-14 text-xs font-medium text-text">{DAY_LABELS[day.day]}</div>
                <div className="relative flex-1 h-5 bg-surface-muted rounded-sm overflow-hidden">
                    {HOUR_TICKS.map((min) => (
                        <div
                            key={min}
                            className="absolute top-0 bottom-0 border-l border-border/50"
                            style={{ left: `${positionPct(min)}%` }}
                        />
                    ))}
                    {day.overlap.map((iv, i) => (
                        <Segment key={`o-${i}`} iv={iv} className="bg-purple-400/50" />
                    ))}
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-stretch gap-2">
            <div className="w-14 pt-1 text-xs font-medium text-text">{DAY_LABELS[day.day]}</div>
            <div className="flex-1 space-y-0.5">
                <BarTrack
                    label="Staff"
                    intervals={day.staff}
                    intervalClass="bg-emerald-400/40"
                    commitments={day.commitments}
                />
                <BarTrack
                    label="Client"
                    intervals={day.client}
                    intervalClass="bg-sky-400/40"
                />
                <BarTrack
                    label="Overlap"
                    intervals={day.overlap}
                    intervalClass="bg-purple-400/50"
                />
            </div>
        </div>
    );
}

function BarTrack({
    label, intervals, intervalClass, commitments,
}: {
    label: string;
    intervals: Interval[];
    intervalClass: string;
    commitments?: CommitmentInterval[];
}) {
    return (
        <div className="flex items-center gap-2">
            <div className="w-12 shrink-0 text-right text-[10px] uppercase tracking-wider text-text-subtle">
                {label}
            </div>
            <div className="relative flex-1 h-5 bg-surface-muted rounded-sm overflow-hidden">
                {HOUR_TICKS.map((min) => (
                    <div
                        key={min}
                        className="absolute top-0 bottom-0 border-l border-border/50"
                        style={{ left: `${positionPct(min)}%` }}
                    />
                ))}
                {intervals.map((iv, i) => (
                    <Segment key={`iv-${i}`} iv={iv} className={intervalClass} />
                ))}
                {commitments?.map((iv, i) => (
                    <Segment
                        key={`m-${i}`}
                        iv={iv}
                        className="commitment-hatch border border-rose-500/70"
                        title={`${iv.clientName} · ${formatMin(iv.startMin)}–${formatMin(iv.endMin)}`}
                    >
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-rose-900 truncate px-1">
                            {iv.clientName}
                        </span>
                    </Segment>
                ))}
            </div>
        </div>
    );
}

function Segment({
    iv, className, title, children,
}: {
    iv: Interval;
    className: string;
    title?: string;
    children?: React.ReactNode;
}) {
    const clippedStart = Math.max(iv.startMin, GRID_START_MIN);
    const clippedEnd = Math.min(iv.endMin, GRID_END_MIN);
    if (clippedEnd <= clippedStart) return null;
    return (
        <div
            className={`absolute top-0 bottom-0 rounded-[3px] ${className}`}
            style={{
                left: `${positionPct(clippedStart)}%`,
                width: `${((clippedEnd - clippedStart) / GRID_SPAN) * 100}%`,
            }}
            title={title}
        >
            {children}
        </div>
    );
}

function positionPct(min: number): number {
    return ((min - GRID_START_MIN) / GRID_SPAN) * 100;
}

function formatHours(minutes: number): string {
    if (minutes <= 0) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
}

function Legend() {
    return (
        <div className="flex flex-wrap items-center gap-4 text-xs text-text-subtle">
            <LegendSwatch className="bg-emerald-400/40" label="Staff available" />
            <LegendSwatch className="bg-sky-400/40" label="Client available" />
            <LegendSwatch className="bg-purple-400/50" label="Overlap" />
            <LegendSwatch className="commitment-hatch border border-rose-500/70" label="Staff committed elsewhere" />
        </div>
    );
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-4 h-3 rounded-sm ${className}`} />
            {label}
        </span>
    );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className="font-display text-xl font-bold mt-1 tabular-nums text-text">{value}</p>
                {hint && <p className="text-2xs text-text-subtle mt-1">{hint}</p>}
            </CardBody>
        </Card>
    );
}
